import type { Event } from '@buzzmap/shared';
import { categoryColors, categoryLabels, formatDateRange } from '../../utils';
import { EventLink } from '../events/EventLink';
import { EventCoverImage } from './EventCoverImage';
import styles from './EventCarousel.module.css';

interface EventCarouselProps {
  events: Event[];
  loading: boolean;
  onEventClick: (id: string) => void;
  onSeeAll: () => void;
  heading?: string;
}

function SkeletonCard() {
  return <div className={styles.skeletonCard} aria-hidden="true" />;
}

/** Larghezza reale della carta: --carousel-card-width (220 px; 60vw sotto 600 px). */
const COVER_SIZES = '(min-width: 600px) 220px, 60vw';

export function EventCarousel({
  events,
  loading,
  onEventClick,
  onSeeAll,
  heading = 'Vicino a te',
}: EventCarouselProps) {
  return (
    <section className={styles.section} aria-label={heading}>
      <div className={styles.header}>
        <h2 className={styles.heading}>{heading}</h2>
        <button
          type="button"
          className={styles.seeAll}
          onClick={onSeeAll}
          aria-label={`Vedi tutti gli eventi ${heading.toLowerCase()}`}
        >
          Vedi tutti &rarr;
        </button>
      </div>

      {loading ? (
        <div className={styles.scrollContainer} aria-busy="true" aria-live="polite">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : events.length === 0 ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyText}>Nessun evento in zona</p>
          <button type="button" className={styles.exploreCta} onClick={onSeeAll}>
            Esplora tutti gli eventi &rarr;
          </button>
        </div>
      ) : (
        <div className={styles.scrollContainer} role="list" aria-label={heading}>
          {events.map((event) => (
            <article
              key={event.id}
              className={`${styles.card} ${styles.hasPhoto}`}
              style={
                {
                  '--card-cat-color': categoryColors[event.category],
                } as React.CSSProperties
              }
              /* R17: keyboard activation moved to the title link - no
                 tabIndex/keydown on the container (no nested interactive
                 roles). role="listitem" stays: it is structural, not
                 interactive. */
              onClick={() => onEventClick(event.id)}
              role="listitem"
            >
              {/* Event image: sponsor photo, then scraped image (https only), then category fallback */}
              {/* Tutte le slide sono lazy: il carosello sta sotto la piega, mai candidato LCP. */}
              <EventCoverImage event={event} className={styles.coverImg} sizes={COVER_SIZES} />

              {/* Bottom info overlay */}
              <div className={styles.cardContent}>
                <span className={styles.categoryBadge}>
                  {categoryLabels[event.category] ?? event.category}
                </span>
                <h3 className={styles.title}>
                  <EventLink eventId={event.id} onClick={() => onEventClick(event.id)} extended>
                    {event.title}
                  </EventLink>
                </h3>
                <p className={styles.meta}>
                  {formatDateRange(event.startDate, event.endDate)}
                  {' \u00B7 '}
                  {event.location.city}
                </p>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
